import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Info, Plus, Trash2, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import CalculatorSaveBar from '../../components/calculators/CalculatorSaveBar';
import useCalculatorSave from '../../components/calculators/useCalculatorSave';

// ─── Sheet catalog (editable prices) ───────────────────────────────
// Residential standard is 1/2" on walls, 5/8" on ceilings. Prices reflect
// Quebec average contractor cost (Apr 2026) — user can override.
const SHEET_OPTIONS = {
  '4x8':  { label: '4′ × 8′',  sqft: 32, default_price: 15 },
  '4x10': { label: '4′ × 10′', sqft: 40, default_price: 19 },
  '4x12': { label: '4′ × 12′', sqft: 48, default_price: 24 },
};

// ─── Finish level (Q1–Q5) per GA-214 / Quebec residential convention ─
// Multipliers applied to the base taping + sanding hours.
const FINISH_LEVELS = {
  '1': {
    label: 'Level 1 — fire-tape only',
    desc: 'Joints taped with 1 coat of compound. No sanding. Garages, plenums.',
    tape_mult: 0.35, sand_mult: 0.15, coats: 1,
  },
  '2': {
    label: 'Level 2 — utility / unfinished',
    desc: '1 coat over tape + fasteners. Light sanding. Storage, mechanical rooms.',
    tape_mult: 0.6, sand_mult: 0.4, coats: 2,
  },
  '3': {
    label: 'Level 3 — textured walls',
    desc: '2 coats + sanding. Joints visible under gloss paint. Will be textured.',
    tape_mult: 0.9, sand_mult: 0.9, coats: 2,
  },
  '4': {
    label: 'Level 4 — standard residential (default)',
    desc: '3 coats + full sanding. Flat / eggshell paint. Quebec residential default.',
    tape_mult: 1.2, sand_mult: 1.3, coats: 3,
  },
  '5': {
    label: 'Level 5 — skim coat / critical lighting',
    desc: 'Full skim coat + fine sanding. Gloss paint, raking light, feature walls.',
    tape_mult: 1.8, sand_mult: 2.3, coats: 4,
  },
};

// Base labour rates (hrs per sqft of net drywall area) — solo installer
const BASE_HANG_HR_PER_SQFT = 0.015;  // ~67 sqft/hr
const BASE_TAPE_HR_PER_SQFT = 0.022;
const BASE_SAND_HR_PER_SQFT = 0.011;

// Material factors (editable)
const DEFAULT_ASSUMPTIONS = {
  screws_lb_per_sqft: 1 / 300,          // 1 lb per 300 sqft (medium field spacing)
  compound_pail_per_sqft: 1 / 400,      // 1 pail (18L) per 400 sqft @ L4
  tape_roll_per_sqft: 1 / 1000,         // 500-ft roll per 1000 sqft
  corner_bead_ft_per_room: 8,           // 4 corners × 8ft default per room (user edits)
  // Prices
  screw_price_per_lb: 8,
  compound_price_per_pail: 18,
  tape_price_per_roll: 6,
  corner_bead_price_per_ft: 0.85,
  waste_pct: 10,
};

// ─── utilities ─────────────────────────────────────────────────────
const fmt = (n, d = 2) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtN = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const uid = () => Math.random().toString(36).slice(2, 9);

export default function DrywallCalculator() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([
    { id: uid(), name: 'Main room', length: 12, width: 10, height: 8, include_ceiling: true },
  ]);
  const [openings, setOpenings] = useState([
    { id: uid(), label: 'Door', width: 3, height: 6.75, count: 1 },
    { id: uid(), label: 'Window', width: 3, height: 4, count: 1 },
  ]);
  const [sheetSize, setSheetSize] = useState('4x8');
  const [sheetPrice, setSheetPrice] = useState(SHEET_OPTIONS['4x8'].default_price);
  const [finishLevel, setFinishLevel] = useState('4');
  const [scope, setScope] = useState('hang_and_finish'); // 'hang_only' | 'finish_only' | 'hang_and_finish'
  const [hourlyRate, setHourlyRate] = useState(65);
  const [markupPct, setMarkup] = useState(20);
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [notes, setNotes] = useState('');

  // ── Save lifecycle ─────────────────────────────────────────────
  const getConfig = useCallback(() => ({
    rooms, openings, sheetSize, sheetPrice, finishLevel, scope,
    hourlyRate, markupPct, assumptions, notes,
  }), [rooms, openings, sheetSize, sheetPrice, finishLevel, scope,
       hourlyRate, markupPct, assumptions, notes]);
  const applyConfig = useCallback((cfg = {}) => {
    if (cfg.rooms !== undefined) setRooms(cfg.rooms);
    if (cfg.openings !== undefined) setOpenings(cfg.openings);
    if (cfg.sheetSize !== undefined) setSheetSize(cfg.sheetSize);
    if (cfg.sheetPrice !== undefined) setSheetPrice(cfg.sheetPrice);
    if (cfg.finishLevel !== undefined) setFinishLevel(cfg.finishLevel);
    if (cfg.scope !== undefined) setScope(cfg.scope);
    if (cfg.hourlyRate !== undefined) setHourlyRate(cfg.hourlyRate);
    if (cfg.markupPct !== undefined) setMarkup(cfg.markupPct);
    if (cfg.assumptions !== undefined) setAssumptions(cfg.assumptions);
    if (cfg.notes !== undefined) setNotes(cfg.notes);
  }, []);

  // Pull user's default hourly rate from job-cost settings (same pattern as Concrete)
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/job-costs/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s && s.default_hourly_rate) setHourlyRate(s.default_hourly_rate); })
      .catch(() => {});
  }, []);

  // When sheet size changes, reset its default price (user can still override)
  useEffect(() => {
    setSheetPrice(SHEET_OPTIONS[sheetSize].default_price);
  }, [sheetSize]);

  // ─── Calculations ──────────────────────────────────────────────
  const calc = useMemo(() => {
    // Wall area = 2(L + W) × H per room; ceiling = L × W when included
    let wallArea = 0;
    let ceilingArea = 0;
    let perimeterFt = 0;
    for (const r of rooms) {
      const L = Number(r.length) || 0;
      const W = Number(r.width) || 0;
      const H = Number(r.height) || 0;
      wallArea += 2 * (L + W) * H;
      perimeterFt += 2 * (L + W);
      if (r.include_ceiling) ceilingArea += L * W;
    }
    const grossArea = wallArea + ceilingArea;

    // Openings subtract from wall area (doors/windows never reduce ceiling)
    let openingsArea = 0;
    for (const o of openings) {
      const w = Number(o.width) || 0;
      const h = Number(o.height) || 0;
      const c = Number(o.count) || 0;
      openingsArea += w * h * c;
    }
    const netArea = Math.max(0, grossArea - openingsArea);
    const adjustedArea = netArea * (1 + assumptions.waste_pct / 100);

    // Sheets (only needed when hanging)
    const sheetSqft = SHEET_OPTIONS[sheetSize].sqft;
    const doesHang = scope !== 'finish_only';
    const doesFinish = scope !== 'hang_only';
    const sheetCount = doesHang ? Math.ceil(adjustedArea / sheetSqft) : 0;

    // Material quantities — based on NET area (waste already in sheets)
    const screwsLb = doesHang ? Math.ceil(netArea * assumptions.screws_lb_per_sqft) : 0;
    const compoundPails = doesFinish ? Math.ceil(netArea * assumptions.compound_pail_per_sqft
      * FINISH_LEVELS[finishLevel].coats / 3) : 0;  // L4 = 3 coats baseline
    const tapeRolls = doesFinish ? Math.ceil(netArea * assumptions.tape_roll_per_sqft) : 0;
    const cornerBeadFt = doesFinish ? Math.ceil(rooms.length * assumptions.corner_bead_ft_per_room) : 0;

    // Material costs
    const matSheets = sheetCount * sheetPrice;
    const matScrews = screwsLb * assumptions.screw_price_per_lb;
    const matCompound = compoundPails * assumptions.compound_price_per_pail;
    const matTape = tapeRolls * assumptions.tape_price_per_roll;
    const matCornerBead = cornerBeadFt * assumptions.corner_bead_price_per_ft;
    const matTotal = matSheets + matScrews + matCompound + matTape + matCornerBead;

    // Labour (hours + $) — gated by scope
    const level = FINISH_LEVELS[finishLevel];
    const hangHrs = doesHang ? netArea * BASE_HANG_HR_PER_SQFT : 0;
    const tapeHrs = doesFinish ? netArea * BASE_TAPE_HR_PER_SQFT * level.tape_mult : 0;
    const sandHrs = doesFinish ? netArea * BASE_SAND_HR_PER_SQFT * level.sand_mult : 0;
    const totalHrs = hangHrs + tapeHrs + sandHrs;
    const labourCost = totalHrs * hourlyRate;

    // Markup + final
    const subtotal = matTotal + labourCost;
    const markupAmt = subtotal * (markupPct / 100);
    const total = subtotal + markupAmt;
    const sqftPrice = netArea > 0 ? total / netArea : 0;

    return {
      wallArea, ceilingArea, grossArea, openingsArea, netArea, adjustedArea,
      perimeterFt, sheetCount, sheetSqft,
      screwsLb, compoundPails, tapeRolls, cornerBeadFt,
      matSheets, matScrews, matCompound, matTape, matCornerBead, matTotal,
      hangHrs, tapeHrs, sandHrs, totalHrs, labourCost,
      subtotal, markupAmt, total, sqftPrice,
    };
  }, [rooms, openings, sheetSize, sheetPrice, finishLevel, scope, hourlyRate, markupPct, assumptions]);

  // ─── Room / opening handlers ───────────────────────────────────
  const addRoom = () => setRooms(r => [...r, {
    id: uid(), name: `Room ${r.length + 1}`,
    length: 10, width: 10, height: 8, include_ceiling: true,
  }]);
  const updateRoom = (id, patch) => setRooms(r => r.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeRoom = (id) => setRooms(r => r.filter(x => x.id !== id));
  const duplicateRoom = (id) => setRooms(r => {
    const idx = r.findIndex(x => x.id === id);
    if (idx < 0) return r;
    const copy = { ...r[idx], id: uid(), name: `${r[idx].name} (copy)` };
    const next = [...r];
    next.splice(idx + 1, 0, copy);
    return next;
  });

  const addOpening = () => setOpenings(o => [...o, {
    id: uid(), label: 'Opening', width: 3, height: 4, count: 1,
  }]);
  const updateOpening = (id, patch) => setOpenings(o => o.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeOpening = (id) => setOpenings(o => o.filter(x => x.id !== id));

  // ─── Save lifecycle (saveApi) ──────────────────────────────────
  const buildSummary = useCallback(() => {
    const level = FINISH_LEVELS[finishLevel];
    const scopeLabel = { hang_and_finish: 'Hang & Finish', hang_only: 'Hang only', finish_only: 'Finish only' }[scope];
    const levelPart = scope === 'hang_only' ? '' : ` · ${level.label}`;
    const text =
`DRYWALL — ${rooms.length} room${rooms.length !== 1 ? 's' : ''} · ${scopeLabel}${levelPart}
Net area: ${fmtN(calc.netArea, 0)} sqft${scope !== 'finish_only' ? ` · Sheets: ${calc.sheetCount} × ${SHEET_OPTIONS[sheetSize].label}` : ''}
Total: ${fmt(calc.total)} (${fmt(calc.sqftPrice)}/sqft)`;
    return {
      text,
      totals: {
        total_quote: calc.total,
        area_ft2: calc.netArea,
        price_per_ft2: calc.sqftPrice,
        labour_cost: calc.labourCost,
        materials_subtotal: calc.matTotal,
        markup_pct: markupPct,
        rooms: rooms.length,
        sheets: calc.sheetCount,
        scope,
      },
    };
  }, [rooms, openings, sheetSize, finishLevel, scope, calc, markupPct]);

  const saveApi = useCalculatorSave({
    calculatorType: 'drywall',
    getConfig,
    applyConfig,
    getSummary: buildSummary,
  });

  // ─── Copy summary (paste into estimate) ────────────────────────
  const copySummary = async () => {
    const level = FINISH_LEVELS[finishLevel];
    const roomsLines = rooms.map(r =>
      `  • ${r.name}: ${r.length}′ × ${r.width}′ × ${r.height}′${r.include_ceiling ? ' (incl. ceiling)' : ''}`
    ).join('\n');
    const openingsLines = openings.length
      ? openings.map(o => `  • ${o.label}: ${o.width}′ × ${o.height}′ × ${o.count}`).join('\n')
      : '  • None';

    const text =
`DRYWALL — ${rooms.length} room${rooms.length !== 1 ? 's' : ''} · ${level.label}

Rooms:
${roomsLines}

Openings subtracted:
${openingsLines}

Area:
  • Wall area:         ${fmtN(calc.wallArea, 0)} sqft
  • Ceiling area:      ${fmtN(calc.ceilingArea, 0)} sqft
  • Openings:         -${fmtN(calc.openingsArea, 0)} sqft
  • Net area:          ${fmtN(calc.netArea, 0)} sqft
  • With ${assumptions.waste_pct}% waste:  ${fmtN(calc.adjustedArea, 0)} sqft

Materials (${SHEET_OPTIONS[sheetSize].label}):
  • Drywall sheets:    ${calc.sheetCount} × ${fmt(sheetPrice)} = ${fmt(calc.matSheets)}
  • Screws:            ${calc.screwsLb} lb × ${fmt(assumptions.screw_price_per_lb)} = ${fmt(calc.matScrews)}
  • Joint compound:    ${calc.compoundPails} pails × ${fmt(assumptions.compound_price_per_pail)} = ${fmt(calc.matCompound)}
  • Joint tape:        ${calc.tapeRolls} rolls × ${fmt(assumptions.tape_price_per_roll)} = ${fmt(calc.matTape)}
  • Corner bead:       ${calc.cornerBeadFt} ft × ${fmt(assumptions.corner_bead_price_per_ft)} = ${fmt(calc.matCornerBead)}
  • Materials total:   ${fmt(calc.matTotal)}

Labour (@ ${fmt(hourlyRate, 2)}/hr):
  • Hanging:   ${fmtN(calc.hangHrs, 1)} hrs
  • Taping:    ${fmtN(calc.tapeHrs, 1)} hrs (${level.coats} coat${level.coats > 1 ? 's' : ''})
  • Sanding:   ${fmtN(calc.sandHrs, 1)} hrs
  • Total:     ${fmtN(calc.totalHrs, 1)} hrs = ${fmt(calc.labourCost)}

Subtotal:            ${fmt(calc.subtotal)}
Markup (${markupPct}%):      ${fmt(calc.markupAmt)}
TOTAL:               ${fmt(calc.total)}
(${fmt(calc.sqftPrice)}/sqft net)
${notes ? `\nNotes: ${notes}` : ''}`;

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Drywall summary copied — paste into an estimate line item.');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" data-testid="drywall-calculator">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/price-book')}
            className="p-2 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E5F0EE] flex items-center justify-center">
              <Layers className="w-5 h-5 text-[#2D6B68]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                Drywall Calculator
              </h1>
              <p className="text-xs text-[#6B7A73] mt-0.5">
                Rooms × finish level → sheets, screws, tape, mud, labour
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={copySummary}
          className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md"
          data-testid="copy-summary-btn"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Summary
        </Button>
      </div>

      <CalculatorSaveBar {...saveApi} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ─── LEFT COLUMN: INPUTS ─────────────────────────────── */}
        <div className="space-y-6">
          {/* Rooms list */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="rooms-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Rooms</h2>
              <Button
                onClick={addRoom}
                variant="ghost"
                size="sm"
                className="text-[#2D6B68] hover:bg-[#F0F3F1]"
                data-testid="add-room-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add room
              </Button>
            </div>

            <div className="space-y-3">
              {rooms.map((r, idx) => (
                <div
                  key={r.id}
                  className="border border-[#E6EBE8] rounded-md p-3 bg-[#F9FAFA]"
                  data-testid={`room-${idx}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Input
                      value={r.name}
                      onChange={e => updateRoom(r.id, { name: e.target.value })}
                      className="flex-1 h-8 text-sm border-[#E6EBE8] bg-white"
                      placeholder="Room name"
                      data-testid={`room-name-${idx}`}
                    />
                    <button
                      onClick={() => duplicateRoom(r.id)}
                      className="p-1.5 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-white rounded"
                      title="Duplicate"
                      data-testid={`room-dup-${idx}`}
                    >
                      <Layers className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeRoom(r.id)}
                      disabled={rooms.length <= 1}
                      className="p-1.5 text-[#6B7A73] hover:text-red-600 hover:bg-white rounded disabled:opacity-30"
                      title="Remove"
                      data-testid={`room-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <DimInput label="Length (ft)" value={r.length} onChange={v => updateRoom(r.id, { length: v })} testid={`room-length-${idx}`} />
                    <DimInput label="Width (ft)" value={r.width} onChange={v => updateRoom(r.id, { width: v })} testid={`room-width-${idx}`} />
                    <DimInput label="Height (ft)" value={r.height} onChange={v => updateRoom(r.id, { height: v })} testid={`room-height-${idx}`} />
                  </div>

                  <label className="flex items-center gap-2 text-xs text-[#3A423E] cursor-pointer">
                    <Checkbox
                      checked={r.include_ceiling}
                      onCheckedChange={v => updateRoom(r.id, { include_ceiling: !!v })}
                      data-testid={`room-ceiling-${idx}`}
                    />
                    Include ceiling
                    <span className="text-[10px] text-[#6B7A73] ml-auto" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {fmtN(2 * (r.length + r.width) * r.height + (r.include_ceiling ? r.length * r.width : 0), 0)} sqft
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Openings list */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="openings-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Openings</h2>
                <p className="text-[11px] text-[#6B7A73] mt-0.5">Doors, windows — subtracted from wall area.</p>
              </div>
              <Button
                onClick={addOpening}
                variant="ghost"
                size="sm"
                className="text-[#2D6B68] hover:bg-[#F0F3F1]"
                data-testid="add-opening-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add opening
              </Button>
            </div>

            {openings.length === 0 ? (
              <p className="text-xs text-[#6B7A73] italic py-2">No openings. Add doors and windows to subtract from sqft.</p>
            ) : (
              <div className="space-y-2">
                {openings.map((o, idx) => (
                  <div
                    key={o.id}
                    className="grid grid-cols-[1fr_80px_80px_60px_32px] gap-2 items-end"
                    data-testid={`opening-${idx}`}
                  >
                    <div>
                      {idx === 0 && <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Label</label>}
                      <Input
                        value={o.label}
                        onChange={e => updateOpening(o.id, { label: e.target.value })}
                        className="h-8 text-sm border-[#E6EBE8]"
                        data-testid={`opening-label-${idx}`}
                      />
                    </div>
                    <NumCell label={idx === 0 ? 'W (ft)' : ''} value={o.width} onChange={v => updateOpening(o.id, { width: v })} testid={`opening-w-${idx}`} />
                    <NumCell label={idx === 0 ? 'H (ft)' : ''} value={o.height} onChange={v => updateOpening(o.id, { height: v })} testid={`opening-h-${idx}`} />
                    <NumCell label={idx === 0 ? 'Count' : ''} value={o.count} onChange={v => updateOpening(o.id, { count: v })} int testid={`opening-count-${idx}`} />
                    <button
                      onClick={() => removeOpening(o.id)}
                      className="h-8 text-[#6B7A73] hover:text-red-600"
                      title="Remove"
                      data-testid={`opening-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mx-auto" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sheet + finish + rates */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5 space-y-4">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Sheet & Finish</h2>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Sheet size</label>
                <Select value={sheetSize} onValueChange={setSheetSize}>
                  <SelectTrigger className="bg-white border-[#E6EBE8] h-9 text-sm" data-testid="sheet-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SHEET_OPTIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label} ({v.sqft} sqft)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Sheet price ($)</label>
                <Input
                  type="number"
                  value={sheetPrice}
                  onChange={e => setSheetPrice(Number(e.target.value) || 0)}
                  className="h-9 text-sm border-[#E6EBE8] bg-white"
                  step="0.5"
                  data-testid="sheet-price-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Scope of work</label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="bg-white border-[#E6EBE8] h-9 text-sm" data-testid="scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hang_and_finish">Hang &amp; Finish</SelectItem>
                  <SelectItem value="hang_only">Hang only</SelectItem>
                  <SelectItem value="finish_only">Finish only (tape, mud, sand)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#6B7A73] mt-1 leading-relaxed">
                {scope === 'hang_only' && 'Sheets, screws & hanging labour only. No tape/mud/sand.'}
                {scope === 'finish_only' && 'Sheets are already up — tape, mud, corner bead, sand labour only.'}
                {scope === 'hang_and_finish' && 'Full install: hang sheets + tape/mud/sand to the chosen finish level.'}
              </p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Finish level</label>
              <Select value={finishLevel} onValueChange={setFinishLevel} disabled={scope === 'hang_only'}>
                <SelectTrigger className="bg-white border-[#E6EBE8] h-9 text-sm disabled:opacity-50" data-testid="finish-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FINISH_LEVELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#6B7A73] mt-1 leading-relaxed">
                {scope === 'hang_only' ? 'Not applicable — sheets only.' : FINISH_LEVELS[finishLevel].desc}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Hourly rate ($)</label>
                <Input
                  type="number"
                  value={hourlyRate}
                  onChange={e => setHourlyRate(Number(e.target.value) || 0)}
                  className="h-9 text-sm border-[#E6EBE8] bg-white"
                  data-testid="hourly-rate-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Markup (%)</label>
                <Input
                  type="number"
                  value={markupPct}
                  onChange={e => setMarkup(Number(e.target.value) || 0)}
                  className="h-9 text-sm border-[#E6EBE8] bg-white"
                  data-testid="markup-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73] flex items-center justify-between">
                <span>Waste factor</span>
                <span className="text-[11px] text-[#3A423E] font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {assumptions.waste_pct}%
                </span>
              </label>
              <Slider
                value={[assumptions.waste_pct]}
                onValueChange={([v]) => setAssumptions(a => ({ ...a, waste_pct: v }))}
                min={0} max={25} step={1}
                className="mt-2"
                data-testid="waste-slider"
              />
            </div>
          </div>

          {/* Assumptions (collapsible-ish) */}
          <details className="bg-white border border-[#E6EBE8] rounded-md" data-testid="assumptions-panel">
            <summary className="px-5 py-3 cursor-pointer text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] select-none">
              Material rates &amp; prices
            </summary>
            <div className="px-5 pb-5 grid grid-cols-2 gap-3 pt-2">
              <AssumpInput label="Screws ($/lb)" value={assumptions.screw_price_per_lb} step="0.5"
                onChange={v => setAssumptions(a => ({ ...a, screw_price_per_lb: v }))} testid="screw-price" />
              <AssumpInput label="Compound ($/pail)" value={assumptions.compound_price_per_pail} step="0.5"
                onChange={v => setAssumptions(a => ({ ...a, compound_price_per_pail: v }))} testid="compound-price" />
              <AssumpInput label="Tape ($/roll)" value={assumptions.tape_price_per_roll} step="0.25"
                onChange={v => setAssumptions(a => ({ ...a, tape_price_per_roll: v }))} testid="tape-price" />
              <AssumpInput label="Corner bead ($/ft)" value={assumptions.corner_bead_price_per_ft} step="0.05"
                onChange={v => setAssumptions(a => ({ ...a, corner_bead_price_per_ft: v }))} testid="bead-price" />
              <AssumpInput label="Corner bead per room (ft)" value={assumptions.corner_bead_ft_per_room} step="1"
                onChange={v => setAssumptions(a => ({ ...a, corner_bead_ft_per_room: v }))} testid="bead-per-room" />
            </div>
          </details>

          {/* Notes */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Notes (included in copy)</label>
            <Input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g., 5/8″ Type X on ceilings, 1/2″ on walls"
              className="mt-2 border-[#E6EBE8] bg-white text-sm"
              data-testid="notes-input"
            />
          </div>
        </div>

        {/* ─── RIGHT COLUMN: BREAKDOWN ──────────────────────────── */}
        <div className="space-y-6 lg:sticky lg:top-4 lg:self-start">
          {/* Area */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="area-summary">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Area</h2>
            <Row label="Wall area" value={`${fmtN(calc.wallArea, 0)} sqft`} />
            <Row label="Ceiling area" value={`${fmtN(calc.ceilingArea, 0)} sqft`} />
            <Row label="Openings" value={`−${fmtN(calc.openingsArea, 0)} sqft`} muted />
            <Row label="Net area" value={`${fmtN(calc.netArea, 0)} sqft`} emphasise />
            <Row label={`With ${assumptions.waste_pct}% waste`} value={`${fmtN(calc.adjustedArea, 0)} sqft`} muted />
          </div>

          {/* Materials */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="materials-summary">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Materials</h2>
            {calc.sheetCount > 0 && (
              <MatRow label={`Drywall sheets (${SHEET_OPTIONS[sheetSize].label})`} qty={`${calc.sheetCount} sheet${calc.sheetCount !== 1 ? 's' : ''}`} cost={calc.matSheets} testid="mat-sheets" />
            )}
            {calc.screwsLb > 0 && (
              <MatRow label="Drywall screws" qty={`${calc.screwsLb} lb`} cost={calc.matScrews} testid="mat-screws" />
            )}
            {calc.compoundPails > 0 && (
              <MatRow label={`Joint compound (${FINISH_LEVELS[finishLevel].coats} coats)`} qty={`${calc.compoundPails} pail${calc.compoundPails !== 1 ? 's' : ''}`} cost={calc.matCompound} testid="mat-compound" />
            )}
            {calc.tapeRolls > 0 && (
              <MatRow label="Joint tape" qty={`${calc.tapeRolls} roll${calc.tapeRolls !== 1 ? 's' : ''}`} cost={calc.matTape} testid="mat-tape" />
            )}
            {calc.cornerBeadFt > 0 && (
              <MatRow label="Corner bead" qty={`${calc.cornerBeadFt} ft`} cost={calc.matCornerBead} testid="mat-bead" />
            )}
            <div className="border-t border-[#E6EBE8] mt-2 pt-2">
              <Row label="Materials subtotal" value={fmt(calc.matTotal)} emphasise />
            </div>
          </div>

          {/* Labour */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="labour-summary">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">
              Labour <span className="normal-case tracking-normal text-[11px] text-[#6B7A73] font-normal">@ {fmt(hourlyRate, 2)}/hr</span>
            </h2>
            {calc.hangHrs > 0 && (
              <MatRow label="Hanging" qty={`${fmtN(calc.hangHrs, 1)} hrs`} cost={calc.hangHrs * hourlyRate} testid="lab-hang" />
            )}
            {calc.tapeHrs > 0 && (
              <MatRow label={`Taping (${FINISH_LEVELS[finishLevel].coats} coat${FINISH_LEVELS[finishLevel].coats > 1 ? 's' : ''})`} qty={`${fmtN(calc.tapeHrs, 1)} hrs`} cost={calc.tapeHrs * hourlyRate} testid="lab-tape" />
            )}
            {calc.sandHrs > 0 && (
              <MatRow label="Sanding" qty={`${fmtN(calc.sandHrs, 1)} hrs`} cost={calc.sandHrs * hourlyRate} testid="lab-sand" />
            )}
            <div className="border-t border-[#E6EBE8] mt-2 pt-2">
              <Row label={`Labour subtotal (${fmtN(calc.totalHrs, 1)} hrs)`} value={fmt(calc.labourCost)} emphasise />
            </div>
          </div>

          {/* Total */}
          <div className="bg-[#2D6B68] text-white rounded-md p-5" data-testid="total-card">
            <div className="flex items-baseline justify-between text-sm opacity-90 mb-1">
              <span>Subtotal</span><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.subtotal)}</span>
            </div>
            <div className="flex items-baseline justify-between text-sm opacity-90 mb-3">
              <span>Markup ({markupPct}%)</span><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.markupAmt)}</span>
            </div>
            <div className="flex items-baseline justify-between border-t border-white/20 pt-3">
              <span className="text-sm uppercase tracking-[0.15em] opacity-90">Total quote</span>
              <span className="text-2xl font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }} data-testid="total-value">
                {fmt(calc.total)}
              </span>
            </div>
            <p className="text-xs opacity-75 mt-1 text-right" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {fmt(calc.sqftPrice)}/sqft net
            </p>
          </div>

          {/* Info */}
          <div className="bg-amber-50/60 border border-amber-200 rounded-md p-3 flex items-start gap-2 text-[11px] text-amber-900">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <p className="leading-relaxed">
              Labour assumes a solo installer. Add 15–25% if you're including helpers or tight-schedule overtime. Finish level multipliers follow GA-214 / standard Quebec residential conventions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small building blocks ─────────────────────────────────────────

function DimInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>
      <Input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value) || 0)}
        className="h-8 text-sm border-[#E6EBE8] bg-white"
        step="0.5"
        min="0"
        data-testid={testid}
      />
    </div>
  );
}

function NumCell({ label, value, onChange, int, testid }) {
  return (
    <div>
      {label && <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>}
      <Input
        type="number"
        value={value}
        onChange={e => onChange(int ? (parseInt(e.target.value, 10) || 0) : (Number(e.target.value) || 0))}
        className="h-8 text-sm border-[#E6EBE8]"
        step={int ? '1' : '0.25'}
        min="0"
        data-testid={testid}
      />
    </div>
  );
}

function AssumpInput({ label, value, step, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>
      <Input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value) || 0)}
        className="h-8 text-sm border-[#E6EBE8] bg-white"
        step={step}
        min="0"
        data-testid={testid}
      />
    </div>
  );
}

function Row({ label, value, muted, emphasise }) {
  return (
    <div className={`flex items-baseline justify-between py-1 ${emphasise ? 'text-[#1A1D1C] font-semibold' : muted ? 'text-[#6B7A73]' : 'text-[#3A423E]'} text-sm`}>
      <span>{label}</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{value}</span>
    </div>
  );
}

function MatRow({ label, qty, cost, testid }) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm" data-testid={testid}>
      <div className="flex-1 min-w-0 pr-2">
        <div className="text-[#3A423E] truncate">{label}</div>
        <div className="text-[11px] text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{qty}</div>
      </div>
      <span className="text-[#1A1D1C] whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {fmt(cost)}
      </span>
    </div>
  );
}
